'use client';

import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isLoginRequiredError, PopupClosedError } from '@xinosolutions/auth-sdk';
import { getXsAuthErrorMessage, useXSAuthContext } from '@xinosolutions/xs-auth-react';
import { registerWithXsAuth } from '../../lib/registerWithXsAuth';

export default function CreateAccountLink({
  className = 'portal-link-xs-btn',
  children = 'Create account',
  redirectTo = '/dashboard',
}) {
  const navigate = useNavigate();
  const { config, establishSession } = useXSAuthContext();
  const [pending, setPending] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [error, setError] = useState('');

  const onCreateAccount = useCallback(async () => {
    setError('');
    setPending(true);
    setPopupOpen(true);

    try {
      const result = await registerWithXsAuth({
        clientId: config.clientId,
        authBaseUrl: config.authBaseUrl,
      });

      if (!result) {
        return null;
      }

      await establishSession(result.idToken, result.user);
      navigate(redirectTo);
      return result;
    } catch (err) {
      if (err instanceof PopupClosedError || isLoginRequiredError(err)) {
        return null;
      }

      const message = getXsAuthErrorMessage(err, 'Could not create account');
      setError(message);
      throw err;
    } finally {
      setPending(false);
      setPopupOpen(false);
    }
  }, [config.authBaseUrl, config.clientId, establishSession, navigate, redirectTo]);

  const pendingLabel = pending
    ? popupOpen
      ? 'Complete signup in the popup…'
      : 'Creating account…'
    : children;

  return (
    <span className="portal-sign-in">
      <button
        type="button"
        className={className}
        disabled={pending}
        onClick={() => {
          onCreateAccount().catch(() => {});
        }}
      >
        {pending ? pendingLabel : children}
      </button>
      {error ? (
        <span className="portal-sign-in-error" role="alert">
          {error}
        </span>
      ) : null}
    </span>
  );
}
